import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { client } from 'src/graphql/client';
import { queryFresh } from 'src/graphql/queryFresh';
import { GET_ROUTES } from 'src/graphql/requests/getRoutes';
import { Route } from 'src/graphql/types/delivery';
import { GGetRoutesQuery } from 'src/graphql/types/generated';
import type { AppDispatch, RootState } from 'src/state/store';
import { naturalSort, updateSequenceNumbersRedelivery } from 'src/utils';
import { isProviderPen } from 'src/utils/thirdPartyDelivery';
import { getAllStopStatuses } from './getAllStopStatues';
import { getPrimaryStopStatuses } from './getPrimaryStopStatuses';
import { DeliveryState } from './state';

// Route membership/tab counts and stop icons are separate Redux projections.
export const refreshRouteData =
  () =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { selectedWarehouseId, nightEnd } = getState().delivery;
    if (!selectedWarehouseId || !nightEnd.viewing) return;
    const input = {
      warehouseId: selectedWarehouseId,
      nightEnd: nightEnd.viewing,
    };
    dispatch(getRoutes(input));
    dispatch(getAllStopStatuses(input));
    dispatch(getPrimaryStopStatuses(input));
  };

type getRoutesArgs = {
  warehouseId: string;
  nightEnd: string;
  cached?: true;
  // Selection thunks start this request before their fulfilled action records
  // the new context. Ordinary refreshes must match the current selection.
  contextChange?: true;
  // A background refresh (useProviderRoutePolling): it never shows the
  // loading state and never raises the route-load error, since the routes on
  // screen are still good when one poll fails.
  poll?: true;
};
// The landing time rides on the action's meta so the reducer stays pure.
type RoutesLandedMeta = { landedAt: number };

export const getRoutes = createAsyncThunk<
  Route[],
  getRoutesArgs,
  { fulfilledMeta: RoutesLandedMeta }
>(
  'delivery/routes',
  async ({ warehouseId, nightEnd }, { fulfillWithValue }) => {
    const { data, error } = await queryFresh<GGetRoutesQuery>(
      client,
      GET_ROUTES,
      { input: { warehouseId, nightEnd } },
    );
    if (error || !data?.routes) {
      throw new Error(error?.message || 'No routes returned');
    }
    return fulfillWithValue(data.routes as Route[], { landedAt: Date.now() });
  },
);

export const prepareRoutes = (input: Route[]): Route[] => {
  // update redelivery stop sequence numbers
  const updatedRoutes = input.map(updateSequenceNumbersRedelivery);

  // Pinned by filter, not a single findIndex: a night can hold several
  // third_party routes, and only the provider staging pens (bucketRoute) are
  // pinned. A route a GM marked for a provider is third_party too but not a
  // bucket, so it stays in the sorted list, and the tabs decide where it
  // shows. Pens are never optimized and are worked from their own tabs
  // (sc-65484); the customer-pickup bucket likewise (Customer Pick Ups).
  const lobbyRoutes = updatedRoutes.filter((route) => route.isLobby);
  const providerPens = updatedRoutes.filter(isProviderPen);
  const customerPickupRoutes = updatedRoutes.filter(
    (route) => !route.isLobby && route.customerPickupRoute,
  );
  const pinned = new Set([
    ...lobbyRoutes,
    ...providerPens,
    ...customerPickupRoutes,
  ]);

  const myRoutes = updatedRoutes
    .filter((route) => !pinned.has(route))
    .sort((aImpl: Route, bImpl: Route) => naturalSort(aImpl.name, bImpl.name));

  return [...pinned, ...myRoutes];
};

export const getRoutesFulfilled = (
  state: DeliveryState,
  action: PayloadAction<Route[]> & {
    meta?: { requestId?: string; arg?: getRoutesArgs; landedAt?: number };
  },
): void => {
  if (
    action.meta?.arg &&
    (action.meta.arg.warehouseId !== state.selectedWarehouseId ||
      action.meta.arg.nightEnd !== state.nightEnd.viewing)
  )
    return;
  if (
    action.meta?.requestId &&
    state.latestRoutesRequestId &&
    action.meta.requestId !== state.latestRoutesRequestId
  ) {
    return;
  }

  state.routes = prepareRoutes(action.payload);
  state.routesLandedAt = action.meta?.landedAt;
  state.routesLoading = false;
  state.routesError = false;
  if (action.meta?.arg) {
    state.routesContext = {
      warehouseId: action.meta.arg.warehouseId,
      nightEnd: action.meta.arg.nightEnd,
    };
  }
};

export const getRoutesPending = (
  state: DeliveryState,
  {
    meta: { arg, requestId },
  }: { meta: { arg: getRoutesArgs; requestId: string } },
): void => {
  if (
    !arg.contextChange &&
    (arg.warehouseId !== state.selectedWarehouseId ||
      arg.nightEnd !== state.nightEnd.viewing)
  )
    return;
  // Disable routes sync button if we request routes
  state.isRoutesSyncEnabled = false;
  // A poll leaves the loading flag to whoever set it: a user's request still
  // in flight keeps its spinner.
  if (!arg.poll) state.routesLoading = arg.cached ? false : true;
  state.latestRoutesRequestId = requestId;
  state.latestRoutesRequestIsPoll = Boolean(arg.poll);
  // A poll fails silently, so it must not take down the error a user's own
  // request raised; only a poll that answers clears it.
  if (!arg.poll) state.routesError = false;
  if (
    state.routesContext?.warehouseId !== arg.warehouseId ||
    state.routesContext?.nightEnd !== arg.nightEnd
  ) {
    state.routes = undefined;
    state.routesContext = undefined;
    state.routesLoading = true;
  }
};

// Never a thunk's request id: once set, every routes response in flight is
// dropped.
const RETIRED_ROUTES_REQUEST = 'retired';

// A reorder or move that lands while a poll is in flight is newer than the
// poll's answer, which may have been read before it: the poll is dropped
// rather than written over the change, and the next tick reads the new order.
export const retireRoutesPoll = (state: DeliveryState): void => {
  if (!state.latestRoutesRequestIsPoll) return;
  state.latestRoutesRequestId = RETIRED_ROUTES_REQUEST;
  state.latestRoutesRequestIsPoll = false;
  // The poll may have superseded a user's request, now dropped with it.
  state.routesLoading = false;
};

export const getRoutesRejected = (
  state: DeliveryState,
  action: { meta?: { requestId?: string; arg?: getRoutesArgs } },
): void => {
  if (
    action.meta?.arg &&
    (action.meta.arg.warehouseId !== state.selectedWarehouseId ||
      action.meta.arg.nightEnd !== state.nightEnd.viewing)
  )
    return;
  if (
    action.meta?.requestId &&
    state.latestRoutesRequestId &&
    action.meta.requestId !== state.latestRoutesRequestId
  ) {
    return;
  }
  state.routesLoading = false;
  // A failed poll keeps the routes on screen and raises nothing; the next
  // tick retries. It still clears loading: as the newest request it has
  // superseded any user request that was in flight.
  if (action.meta?.arg?.poll) return;
  state.routesError = true;
};
